import { useEffect, useState } from "react";
import AppUser from "../../api/app-user.class";

import "./image-history.scss";
import "../../components/modal.scss";
import { useDialogo, aoClicarNoFundo } from "../../components/use-dialogo";
import "./aviso-de-recusa.scss";
import Utils from "../../classes/utils.class";
import EditBgRm from "./edit-bg-rm";
import { PopUpInfo } from "../../components/confirm-pop-up";

type PropsType = {
  close: (popupInfo?: PopUpInfo) => void;
  user: AppUser;
};

/**
 * O HISTÓRICO, E O QUE ELE ERA.
 *
 * T1.22 do pedido: "histórico péssimo de visualizar, mas útil". A tela era doze linhas que
 * imprimiam `${i + 1}: ${img.prompt}` — uma lista de textos numerados, centralizados, com o
 * número não significando nada. Sem miniatura, sem data, sem estado.
 *
 * O pior não era a feiura: o pedido que estava DESENHANDO e o que foi RECUSADO pela barreira
 * apareciam exatamente iguais aos que deram certo. Clicar num deles abria um seletor de imagens
 * vazio, e a criança não tinha como saber por quê. Isso só ficou visível quando eu fabriquei um
 * histórico com os três estados na vitrine — antes disso eu tinha marcado este ponto como
 * "precisa de conta com histórico real", que era uma desculpa e não uma limitação.
 *
 * Agora cada linha diz o estado por escrito, com forma e cor, e o que não tem imagem não é
 * clicável.
 */
/** O nome que a pessoa vê de cada prateleira. O mesmo do `submenu.tsx`, e de propósito. */
const NOME_DA_PRATELEIRA: Record<"characters" | "scenarios" | "figures", string> = {
  characters: "Personagens",
  scenarios: "Cenários",
  figures: "Figuras",
};

const ROTULO_ESTADO: Record<string, string> = {
  ok: "pronto",
  generating: "desenhando",
  blocked: "recusado",
};

/**
 * O ENDEREÇO DE UMA IMAGEM DO HISTÓRICO, E O DEFEITO QUE ESTA FUNÇÃO EXISTE PARA MATAR.
 *
 * `T4.11`, na letra: *"Quando eu clico aqui na imagem que eu já gerei / Ele diz imagem gerada e
 * supostamente está aqui / Mas eu não consigo ver, está bugado, estou perdendo a imagem"*.
 *
 * A tela fazia isto, em dois lugares:
 *
 *     src={id.startsWith("http") ? id : `https://tse.mm.bing.net/th/id/${id}`}
 *
 * O prefixo do Bing é de um provedor que **não existe mais** — o próprio `api/imgen.ts` diz, por
 * escrito, que "chaves por-usuario (Pixazo, Bing) foram removidas, providers descontinuados no
 * backend". O que o backend devolve hoje é uma URI de dados, MEDIDO em 13/09 contra produção:
 *
 *     {"images":["data:image/png;base64,iVBORw0KGgo..."]}
 *
 * `"data:...".startsWith("http")` é **falso**. Então toda imagem do histórico virava
 * `https://tse.mm.bing.net/th/id/data:image/png;base64,...` — um endereço que não carrega nada.
 *
 * A tela dizia "Imagens geradas:" e mostrava retângulos vazios. É a frase dele inteira, incluindo
 * a parte do botão: clicar num retângulo quebrado leva a OUTRO retângulo quebrado, e "Voltar"
 * devolve à mesma tela vazia — de onde ele está, o botão não faz nada.
 *
 * O ramo do Bing fica, e só ele, para o histórico ANTIGO de quem tenha ids daquela época. Não
 * carrega mais nada também, mas remover o ramo não conserta esse caso e mudaria o comportamento
 * de dados que eu não posso ver.
 */
function enderecoDaImagem(id: string): string {
  if (id.startsWith("data:") || id.startsWith("http")) return id;
  return `https://tse.mm.bing.net/th/id/${id}`;
}

/** "há 3 minutos". Data absoluta num histórico de sessão não ajuda ninguém a se localizar. */
function tempoRelativo(quando: number): string {
  const s = Math.max(0, Math.round((Date.now() - quando) / 1000));
  if (s < 60) return "agora";
  const min = Math.round(s / 60);
  if (min < 60) return `há ${min} ${min === 1 ? "minuto" : "minutos"}`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} ${h === 1 ? "hora" : "horas"}`;
  const d = Math.round(h / 24);
  return `há ${d} ${d === 1 ? "dia" : "dias"}`;
}

/**
 * A MINIATURA DE UM PEDIDO, COM O CASO DE ELA NÃO CARREGAR — `T5.12`.
 *
 * ── POR QUE ISTO É UM COMPONENTE, E NÃO UM `<img>` NA LINHA ─────────────────────────────────
 *
 * Medido em 27/09, logo depois de a miniatura entrar: **3 miniaturas na lista e 2 desenhadas**. A
 * terceira é de um pedido com id da época do provedor antigo — o `enderecoDaImagem` manda ids que
 * não são `data:` nem `http` para o prefixo do Bing, que foi removido, e a imagem não carrega.
 *
 * Isso já era verdade no DETALHE, e está escrito no `TS-T4.4.2`. O que a miniatura mudou foi a
 * VISIBILIDADE: antes o id morto quebrava depois de um clique, e agora quebraria na lista, para
 * todo mundo que tenha histórico daquela época, na hora de abrir o painel.
 *
 * **Miniatura quebrada é pior que miniatura nenhuma**: um quadrado com o ícone de imagem partida
 * não diz nada sobre o pedido, e ainda sugere que o produto perdeu o desenho. Então quando a imagem
 * falha, a linha volta a mostrar o mesmo sinal de quem não tem imagem — e o texto da linha, que diz
 * `4 imagens`, continua contando a verdade: as imagens existem, e o endereço delas é que não serve.
 *
 * E o `onError` é o único jeito de saber: `naturalWidth` só responde depois do carregamento, e
 * `enderecoDaImagem` não tem como saber se o id antigo ainda resolve sem tentar.
 */
function MiniaturaDoPedido({ endereco }: { endereco: string }) {
  const [falhou, setFalhou] = useState(false);
  if (falhou) return <span className="historico-sem-imagem">✕</span>;
  return (
    <img
      src={endereco}
      alt=""
      draggable={false}
      loading="lazy"
      onError={() => setFalhou(true)}
    />
  );
}

function ImageHistory({ close, user }: PropsType) {
  const [selectedPromptRequestId, setSelectedPromptRequestId] = useState<
    string | null
  >(null);
  const [selectedImgUrl, setSelectedImgUrl] = useState<undefined | string>(
    undefined
  );
  const [filteredImgUrl, setFilteredImgUrl] = useState<
    undefined | null | string
  >(undefined);
  const [imgType, setImgType] = useState<
    "characters" | "scenarios" | "figures"
  >("characters");
  const [isSavingImg, setIsSavingImg] = useState(false);
  /**
   * QUALQUER erro que esta tela mostra na propria faixa — o do recorte e o do salvamento.
   *
   * Ja se chamou `erroDoRecorte`, e o nome passou a mentir no dia em que o segundo erro entrou aqui.
   * Nome que mente e o mesmo defeito de um comentario vencido, com a mesma cara de correto.
   */
  const [erroNaTela, setErroNaTela] = useState<string | null>(null);
  /**
   * EM QUE PASSO O RECORTE ESTA — `T4.8`, e antes disto a tela ficava muda por minutos.
   *
   * Medido em 28/09 na vitrine: com uma imagem de 256x256, o recorte rodou **180 s sem terminar e sem
   * falhar**, e nesse tempo a tela nao mudou uma palavra. A biblioteca baixa 46 MB de WASM e 84 MB de
   * modelo antes de comecar a pensar, e ela AVISA em que arquivo esta — o `progress` sempre existiu e
   * ninguem o consumia. E a mesma forma do "ta salvando infinitamente" do `T6.4`, e o mesmo conserto:
   * o laco sabe onde esta, e o que faltava era ele CONTAR.
   */
  const [passoDoRecorte, setPassoDoRecorte] = useState<string | null>(null);

  const selectedImg = !selectedPromptRequestId
    ? null
    : user.GeneratedImages()[selectedPromptRequestId];

  useEffect(() => {
    localStorage.removeItem("edited-bg-rm-img");
  }, []);

  // T8.5/T8.18 · Esc fecha e a pagina de tras nao rola. O porque das tres coisas esta em
  // `usar-dialogo.ts`; o que decide aqui e `isSavingImg`: fechar no meio de um salvamento perderia
  // a imagem sem avisar, que e o defeito que este achado descreve por outro caminho.
  useDialogo(() => {
    if (!isSavingImg) close();
  });

  return (
    // T8.18 · a casca compartilhada (`modal.scss`), o Esc e o clique fora. As tres queixas deste
    // achado — titulo a direita, X sem estilo sobre a borda, e a tela toda para uma frase — moravam
    // todas na casca, e a casca era copia. O porque de cada linha esta no `modal.scss`.
    <div
      className="generate-image-bg hq-modal-fundo"
      onClick={aoClicarNoFundo(() => {
        if (!isSavingImg) close();
      })}
    >
      <div
        className="generate-image-container hq-modal-caixa"
        role="dialog"
        aria-modal="true"
        aria-label="Seus pedidos de desenho"
      >
        <div className="header hq-modal-topo">
          {/* O TITULO VEM PRIMEIRO, e isto e o conserto de "o titulo fica encostado a direita": num
              `space-between`, quem vem primeiro no HTML vai para a esquerda. O X vinha antes. */}
          {!selectedPromptRequestId && <h3>Seus pedidos de desenho</h3>}

          {!!selectedPromptRequestId && (
            <div>
              <button
                style={{ margin: ".8vw 1vw" }}
                disabled={isSavingImg || filteredImgUrl === null}
                onClick={() => {
                  localStorage.removeItem("edited-bg-rm-img");
                  if (!selectedImgUrl) {
                    setSelectedPromptRequestId(null);
                  } else {
                    setSelectedImgUrl(undefined);
                    setFilteredImgUrl(undefined);
                  }
                }}
              >
                Voltar
              </button>

              {selectedImgUrl && (
                <>
                  <button
                    style={{ margin: ".8vw 1vw" }}
                    disabled={filteredImgUrl !== undefined || isSavingImg}
                    onClick={() => {
                      setErroNaTela(null);
                      setFilteredImgUrl(null);
                      setPassoDoRecorte("Buscando o recortador…");
                      Utils.removeBG(selectedImgUrl, (passo, atual, total) => {
                        // A biblioteca chama isto por ARQUIVO, e o nome do arquivo e um hash — nao
                        // serve para a crianca. O que serve e o tamanho: "23 MB de 130" diz se vale
                        // esperar, e o nome do hash nao diz nada.
                        const mb = (n: number) => Math.round(n / 1048576);
                        setPassoDoRecorte(
                          total > 1048576
                            ? `Buscando o recortador… ${mb(atual)} MB de ${mb(total)}`
                            : /fetch|download/i.test(passo)
                              ? "Buscando o recortador…"
                              : "Tirando o fundo…"
                        );
                      })
                        .then((res: any) => {
                          setPassoDoRecorte(null);
                          if (selectedImgUrl) setFilteredImgUrl(res);
                        })
                        .catch((err: any) => {
                          // ERA `alert(...)` SEGUIDO DE `close()`: a falha do recorte FECHAVA O
                          // HISTORICO INTEIRO. Quem so queria ver a imagem perdia a tela, e a
                          // unica explicacao era um alerta que some no primeiro OK.
                          //
                          // E a falha nao e rara: medido em 13/09, o recorte NUNCA roda — a
                          // biblioteca busca o WASM no unpkg em tempo de execucao e o navegador
                          // barra por CORS. Detalhe em `rastro/21-o-recorte-de-fundo...md`.
                          console.error("[remover-fundo]", err);
                          setPassoDoRecorte(null);
                          setFilteredImgUrl(undefined);
                          setErroNaTela(
                            "Não deu para tirar o fundo desta imagem agora. Ela continua aqui " +
                              "do jeito que veio — você pode usá-la assim."
                          );
                        });
                    }}
                  >
                    Remover Fundo
                  </button>
                  {/* A REGUA DO RECORTE. `aria-live="polite"` porque quem usa leitor de tela e quem
                      mais sofre com tela calada, e `polite` porque isto nao interrompe nada. */}
                  {passoDoRecorte && (
                    <p className="historico-passo-do-recorte" aria-live="polite">
                      {passoDoRecorte}
                    </p>
                  )}

                  <select
                    value={imgType}
                    onChange={(e) => setImgType(e.target.value as any)}
                  >
                    <option value="characters">Personagem</option>
                    <option value="scenarios">Cenario</option>
                    <option value="figures">Figura</option>
                  </select>

                  <button
                    style={{ margin: ".8vw 1vw" }}
                    disabled={filteredImgUrl === null || isSavingImg}
                    onClick={async () => {
                      setIsSavingImg(true);
                      try {
                        let editedBgRmImg =
                          localStorage.getItem("edited-bg-rm-img");
                        localStorage.removeItem("edited-bg-rm-img");
                        await user.saveImg(
                          editedBgRmImg || filteredImgUrl || selectedImgUrl,
                          imgType
                        );
                        close({
                          type: "alert",
                          // ── DIZER ONDE FOI, E NAO SO QUE FOI (T5.13) ───────────────────
                          //
                          // Era "Imagem salva!", sem prateleira. E o seletor acima tem padrao
                          // `characters`: quem gera cinco desenhos sem mexer nele manda os cinco
                          // para Personagens, vai procurar em Figuras e acha "so algumas coisas"
                          // — que e a frase dele.
                          //
                          // A outra tela que salva imagem (`criar-comic.tsx`) ja nomeava a
                          // prateleira desde o T2. Duas telas que fazem a mesma coisa e contam
                          // de formas diferentes e como se metade do produto tivesse a licao.
                          text: `Imagem salva em ${NOME_DA_PRATELEIRA[imgType]}! Você a encontra nessa prateleira do editor.`,
                          close: () => {},
                          confirm: () => {},
                        });
                      } catch (err) {
                        // T8.31 · ERA `alert("Falha ao tentar salvar imagem")`, e sobrou desta tela
                        // quando as outras sete trocaram: dialogo do navegador em cima de dialogo do
                        // produto, com texto que nao diz o que fazer.
                        //
                        // E a mensagem fica AQUI DENTRO, na mesma faixa do erro do recorte, e nao
                        // por `close({...})`: fechar o historico numa falha e o defeito que a linha
                        // 174 deste arquivo descreve — quem so queria ver a imagem perde a tela.
                        console.error("[historico] falha ao salvar imagem", err);
                        setErroNaTela(
                          "Não deu para guardar esta imagem agora. Ela continua aqui na tela — " +
                            "tente de novo em instantes."
                        );
                      } finally {
                        setIsSavingImg(false);
                      }
                    }}
                  >
                    Salvar imagem
                  </button>
                </>
              )}
            </div>
          )}

          {/* T8.18 · O X POR ULTIMO, e com estilo proprio (`.hq-modal-fechar`). O achado diz que ele
              "e o padrao do navegador e fica sobre a borda esquerda": as duas coisas eram a mesma —
              `.close-btn` nao tinha regra nenhuma, e vinha primeiro no HTML.
              Era um `<h3 onClick>` antes disso: nao chegava por Tab, nao disparava com Enter, e se
              anunciava como titulo. */}
          <button
            type="button"
            className="close-btn hq-modal-fechar"
            aria-label="Fechar o histórico"
            title="Fechar o histórico"
            disabled={isSavingImg}
            onClick={() => close()}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        {!selectedPromptRequestId && (
          <div className="content historico-lista hq-modal-corpo">
            {Object.entries(user.GeneratedImages()).length === 0 && (
              <p className="historico-vazio">
                Você ainda não gerou nenhuma imagem. Quando gerar, elas ficam guardadas aqui
                para você usar de novo sem precisar pedir outra vez.
              </p>
            )}

            {Object.entries(user.GeneratedImages())
              .sort((a, b) => b[1].createdAt - a[1].createdAt)
              .map(([requestId, img]) => {
                const pronto = img.status === "ok" && img.ids.length > 0;
                return (
                  <button
                    key={requestId}
                    type="button"
                    className={`historico-item historico-${img.status}`}
                    // Recusado e "desenhando" não abrem nada: não há imagem do outro lado.
                    // Deixar o clique morrer em silêncio é pior que não ter clique.
                    disabled={!pronto}
                    onClick={() => setSelectedPromptRequestId(requestId)}
                  >
                    {/* ── T5.12 · A REFERÊNCIA VISUAL, E ELA É A PRIMEIRA COISA DA LINHA ──────────

                        Ele, na letra: *"seria bom ter uma referência visual do que foi gerada.
                        Tipo, é… **última geração minha, aí aparece um quadradinho**."*

                        E a decisão de FAZER já estava escrita no `TS-T5.7.2`, com o corte que a
                        destravou: no histórico a miniatura é barata e **não expõe nada** — as
                        imagens já estão em `users/{uid}/imgs`, privadas por regra, e esta tela já
                        as mostra no detalhe. O que continua sendo decisão dele é a CAPA NA FICHA
                        PÚBLICA, que é outra coisa: põe imagem num documento legível sem conta.

                        A PRIMEIRA imagem do pedido, e não todas: a linha responde "o que saiu
                        disto?" num relance, e o detalhe continua sendo onde se vê o conjunto.

                        E QUEM NÃO TEM IMAGEM NÃO GANHA QUADRADO VAZIO: um espaço vazio do tamanho
                        de uma miniatura é indistinguível de uma imagem que não carregou. Quem está
                        desenhando ou foi recusado recebe um sinal que DIZ o que é. */}
                    <span className="historico-miniatura" aria-hidden="true">
                      {pronto ? (
                        <MiniaturaDoPedido endereco={enderecoDaImagem(img.ids[0])} />
                      ) : (
                        <span className="historico-sem-imagem">
                          {img.status === "generating" ? "…" : "✕"}
                        </span>
                      )}
                    </span>
                    <span className="historico-estado">{ROTULO_ESTADO[img.status]}</span>
                    <span className="historico-prompt">{img.prompt}</span>
                    <span className="historico-meta">
                      {pronto
                        ? `${img.ids.length} ${img.ids.length === 1 ? "imagem" : "imagens"}`
                        : img.status === "generating"
                        ? "ainda desenhando"
                        : "nenhuma imagem"}
                      {" · "}
                      {tempoRelativo(img.createdAt)}
                    </span>
                  </button>
                );
              })}
          </div>
        )}

        {selectedImg && (
          <div className="content hq-modal-corpo">
            <h4>{selectedImg.prompt}</h4>

            {erroNaTela && (
              <p className="gerar-recusa" role="alert">
                {erroNaTela}
              </p>
            )}

            {selectedImg.status === "ok" && !selectedImgUrl && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <h2>Imagens geradas:</h2>
                <div className="imgs-container">
                  {selectedImg.ids.map((id: string) => (
                    <img
                      key={id}
                      src={enderecoDaImagem(id)}
                      // Era `alt={id}` — e `id` agora é uma URI de dados de centenas de
                      // milhares de caracteres. Um leitor de tela leria base64 em voz alta, e
                      // com a imagem quebrada era isso que aparecia escrito na tela.
                      alt={`Imagem para "${selectedImg.prompt}"`}
                      className="generated-img hover-expand-effect"
                      draggable={false}
                      onClick={() => setSelectedImgUrl(enderecoDaImagem(id))}
                    />
                  ))}
                </div>
              </div>
            )}

            {!!selectedImgUrl && filteredImgUrl === undefined && (
              <div className="imgs-container">
                <img
                  src={selectedImgUrl}
                  alt="Imagem Gerada"
                  className="generated-img"
                  draggable={false}
                />
              </div>
            )}
            {filteredImgUrl === null && <h3>Removendo fundo...</h3>}
            {!!selectedImgUrl && !!filteredImgUrl && (
              <EditBgRm imgSrc={selectedImgUrl} imgBgRmSrc={filteredImgUrl} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageHistory;
